// Seller, visão geral (`spec/24-croqui-alinhamento.md` §5, WEB-51 original; port Zenith
// WEB-102). Igual ao frame `screens/seller/visao-geral.tsx` do croqui em 1440.
//
// Herói = "Vendas no período". O saldo NÃO mora mais aqui (o croqui não tem bloco de
// saldo nesta tela): ele é `/app/balance` e o "Recebido no mês" da sidebar.
//
// Três leituras de `reports/summary`: a do filtro, a do período anterior (as comparações
// de delta dos KPIs) e uma janela FIXA de 30 dias, que alimenta o aviso de chargeback e o
// KPI "Hoje", nenhum dos dois segue o filtro da tela. Quando o filtro já é 30 dias, o
// React Query serve a terceira do mesmo cache.
//
// `GET /v1/me/kyc` fica de fora de propósito: ela cria uma submissão `draft`
// (`spec/08` §4.2) e a tela abriria um rascunho de KYC a cada carregamento.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { apiKeysQueryOptions } from '../api/queries/api-keys.ts';
import {
  countersQueryOptions,
  last30Range,
  previousRange,
  profileQueryOptions,
  recentTransactionsQueryOptions,
  summaryQueryOptions,
} from '../api/queries/overview.ts';
import { endpointsQueryOptions } from '../api/queries/webhooks.ts';
import { DOCS_BASE_URL } from '../app/config.ts';
import { useMode } from '../app/mode.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { Button } from '../components/ui/button.tsx';
import { BalanceNow } from '../features/overview/balance-now.tsx';
import { DisplayNameCard } from '../features/overview/display-name-card.tsx';
import { FollowupCard } from '../features/overview/followup-card.tsx';
import {
  ChargebackNotice,
  ProfileErrorNotice,
  StatusNotice,
} from '../features/overview/notices.tsx';
import { HealthSection, PendingSection, VolumeSection } from '../features/overview/panels.tsx';
import { salesTitle } from '../features/overview/period.ts';
import { PeriodControl } from '../features/overview/period-control.tsx';
import { MobileOverview } from '../features/overview/piao/mobile-overview.tsx';
import { RecentTransactions } from '../features/overview/recent-transactions.tsx';
import { resolveRange, validateOverviewSearch } from '../features/overview/search.ts';
import { OverviewStats, OverviewStatsSkeleton } from '../features/overview/stats.tsx';
import { todayIsoDate } from '../lib/dates.ts';
import { keepWhileSameMode } from '../lib/keep-previous.ts';
import { useMediaQuery } from '../lib/use-media-query.ts';

/**
 * Só UMA árvore montada por vez (WEB-119): abaixo de `lg` (`64rem`, o breakpoint do Zenith)
 * é o dashboard mobile "pião" do canvas `product:leztypay`; acima é a árvore de hoje,
 * intocada (`DesktopOverview`, o corpo antigo desta função).
 */
function Overview() {
  const isMobile = useMediaQuery('(width < 64rem)');
  const range = resolveRange(Route.useSearch());
  const navigateHere = Route.useNavigate();
  const navigate = useNavigate();

  if (isMobile) {
    return (
      <MobileOverview
        range={range}
        onRangeChange={(next) => {
          void navigateHere({ search: next, replace: true });
        }}
        onNavigate={(to) => {
          void navigate({ to });
        }}
      />
    );
  }
  return <DesktopOverview />;
}

function DesktopOverview() {
  const range = resolveRange(Route.useSearch());
  const navigateHere = Route.useNavigate();
  const navigate = useNavigate();
  const { mode } = useMode();
  const today = todayIsoDate();

  // Trocar o período não pisca o skeleton: o resumo anterior fica na tela (esmaecido) e os
  // números contam do valor antigo até o novo.
  const keep = keepWhileSameMode(mode);
  const summary = useQuery({ ...summaryQueryOptions(mode, range), placeholderData: keep });
  const previous = useQuery({
    ...summaryQueryOptions(mode, previousRange(range)),
    placeholderData: keep,
  });
  const last30 = useQuery(summaryQueryOptions(mode, last30Range(today)));
  const charges = useQuery(recentTransactionsQueryOptions(mode, today));
  const profile = useQuery(profileQueryOptions(mode));
  const counters = useQuery(countersQueryOptions(mode));
  const apiKeys = useQuery(apiKeysQueryOptions());
  const endpoints = useQuery(endpointsQueryOptions(mode));

  const go = (to: string): void => {
    void navigate({ to });
  };

  const todayGrossCents = last30.data?.daily.find((day) => day.date === today)?.gross_cents ?? 0;

  const balanceNow = (
    <BalanceNow
      balance={summary.data?.balance_now}
      onOpen={() => {
        go('/app/balance');
      }}
    />
  );

  return (
    <>
      <PageActions>
        <PeriodControl
          value={range}
          onChange={(next) => {
            void navigateHere({ search: next, replace: true });
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          type="button"
          className="hidden sm:inline-flex"
          onClick={() => {
            go('/app/reports');
          }}
        >
          Exportar
        </Button>
        <Button
          size="sm"
          type="button"
          onClick={() => {
            go('/app/withdrawals');
          }}
        >
          Sacar
        </Button>
      </PageActions>

      {profile.isError ? (
        <ProfileErrorNotice
          error={profile.error}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void profile.refetch();
              }}
            >
              Tentar de novo
            </Button>
          }
        />
      ) : (
        profile.data !== undefined && <StatusNotice status={profile.data.status} />
      )}

      {profile.data !== undefined && <DisplayNameCard profile={profile.data} mode={mode} />}
      {profile.data !== undefined && (
        <FollowupCard profile={profile.data} mode={mode} onNavigate={go} />
      )}

      {last30.data !== undefined && profile.data !== undefined && (
        <ChargebackNotice
          chargebacks={last30.data.chargebacks}
          autoApprove={profile.data.withdraw_auto_approve}
          action={
            <Button
              variant="secondary"
              size="xs"
              onClick={() => {
                void navigate({ to: '/app/transactions', search: { status: 'chargeback' } });
              }}
            >
              Ver transações
            </Button>
          }
        />
      )}

      {/* Primeiro o que entrou e o que dá pra sacar; o gráfico do período mora dentro do
          cartão de vendas. Erro do resumo aparece uma vez só, no lugar do bloco inteiro. */}
      {summary.data !== undefined ? (
        <div
          className={`transition-opacity duration-200 ${summary.isPlaceholderData ? 'opacity-60' : ''}`}
          aria-busy={summary.isPlaceholderData}
        >
          <OverviewStats
            title={salesTitle(range)}
            summary={summary.data}
            previous={previous.data}
            todayGrossCents={todayGrossCents}
            aside={balanceNow}
            chart={
              <VolumeSection
                summary={summary.data}
                range={range}
                isPending={summary.isPending}
                error={summary.error}
                onRetry={() => {
                  void summary.refetch();
                }}
              />
            }
          />
        </div>
      ) : summary.isError ? (
        <ErrorState
          error={summary.error}
          onRetry={() => {
            void summary.refetch();
          }}
        />
      ) : (
        <OverviewStatsSkeleton aside={balanceNow} />
      )}

      <div className="grid min-h-0 flex-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          <RecentTransactions
            page={charges.data}
            isPending={charges.isPending}
            error={charges.error}
            onRetry={() => {
              void charges.refetch();
            }}
            onOpen={(id) => {
              go(`/app/transactions/${id}`);
            }}
            seeAll={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  go('/app/transactions');
                }}
              >
                Ver todas
              </Button>
            }
            emptyAction={
              <Button asChild variant="outline" size="sm">
                <a href={`${DOCS_BASE_URL}/quickstart/`} target="_blank" rel="noreferrer">
                  Ver a documentação da API
                </a>
              </Button>
            }
          />
        </div>

        <div
          className="rise-in grid gap-6 md:grid-cols-2 xl:grid-cols-1"
          style={{ animationDelay: '160ms' }}
        >
          <PendingSection
            counters={counters.data}
            kycStatus={profile.data?.kyc_status}
            isPending={counters.isPending}
            error={counters.error}
            onRetry={() => {
              void counters.refetch();
            }}
            onNavigate={go}
          />
          <HealthSection
            counters={counters.data}
            apiKeys={apiKeys.data}
            endpoints={endpoints.data}
            mode={mode}
            isPending={counters.isPending && apiKeys.isPending && endpoints.isPending}
            error={apiKeys.error ?? endpoints.error}
            onRetry={() => {
              void apiKeys.refetch();
              void endpoints.refetch();
            }}
            onNavigate={go}
          />
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/app/')({
  component: Overview,
  validateSearch: validateOverviewSearch,
});
